import { Button } from '@material-ui/core'
import dayjs from 'dayjs'
import React from 'react'

import { switchInvoiceBillingMode } from 'api'
import constants from 'app/constants'
import {
  cartStatusTypes,
  commonAttributes,
  navigationPaths,
  tableColumnDefaultOption
} from 'app/config'
import { confirm, eventAlert } from 'app/helpers'

// Feature 3 — confirmation copy for the mid-admission daycare toggle.
// Reissues the pending instalment invoice under the new tax mode;
// past paid invoices remain untouched (Decision #3).
const SWITCH_BILLING_CONFIRM_MESSAGE =
  'This will cancel the current pending invoice (Razorpay link will be invalidated) and re-issue it with the new tax treatment. Past paid invoices for this admission will not be affected. Future term invoices will use the new mode. Continue?'

const handleSwitchBillingMode = (invoiceUuid) => {
  confirm(
    async () => {
      try {
        const response = await switchInvoiceBillingMode(invoiceUuid)
        eventAlert(
          `Billing mode switched. New invoice: ${response?.new_invoice_uuid || ''}`,
          constants.SUCCESS
        )
        // The list page lives behind redux-saga refresh; the user will
        // re-pull on next filter / refresh. Avoid forcing a global
        // reload here so unsaved filters survive.
      } catch (error) {
        eventAlert(
          error?.response?.data?.error ||
            error?.message ||
            'Failed to switch billing mode',
          constants.ERROR
        )
      }
    },
    SWITCH_BILLING_CONFIRM_MESSAGE,
    () => {},
    {
      title: 'switch billing mode?',
      confirmButtonText: 'switch'
    }
  )
}

export const columns = [
  {
    ...commonAttributes.date,
    name: 'created_date',
    label: 'Order Date',
    options: {
      ...commonAttributes.date.options,
      customBodyRender: (val) => dayjs(val).format('DD MMM, YYYY'),
      filterKey: 'created_date'
    }
  },
  {
    name: 'parent',
    label: 'Parent Name',
    options: {
      ...tableColumnDefaultOption.options,
      customBodyRender: (value) =>
        value ? (
          <span>
            <a
              href={`${navigationPaths.parentdashboard}?parent=${value.user_id}`}
            >
              {`${value?.user?.full_name}`}
            </a>
          </span>
        ) : null,
      filterKey: 'parent_name'
    }
  },
  {
    name: 'parent.user.phone_number',
    label: 'Parent Number',
    options: {
      ...tableColumnDefaultOption.options,
      filterKey: 'phone_number'
    }
  },
  {
    ...commonAttributes.centre,
    name: 'parent.centre_preference.name',
    label: 'Centre',
    options: {
      ...tableColumnDefaultOption.options,
      filterKey: 'centre_name'
    }
  },
  {
    name: 'status',
    label: 'Order Status',
    widget: 'select',
    widgetFilter: true,
    isValueKey: true,
    valueLabelMappings: cartStatusTypes,
    options: { ...tableColumnDefaultOption.options, filterKey: 'status' }
  },
  {
    name: 'value',
    label: 'Total Payable Amount',
    options: {
      ...tableColumnDefaultOption.options,
      disableFilter: true,
      customBodyRender: (value) =>
        value ? <span>{parseFloat(value).toFixed(2)}</span> : null
    }
  },
  // Feature 7b — late-fee column on the Instalment Orders list. The
  // backend (`Invoice.late_fee_amount`) holds the running total accrued
  // by the daily cron; ops can spot overdue admissions at a glance. The
  // accompanying select filter exposes a "With late fee" chip that hits
  // the backend's `with_late_fee` filter key.
  {
    name: 'late_fee_amount',
    label: 'Late fee',
    widget: 'select',
    widgetFilter: true,
    isValueKey: true,
    valueLabelMappings: {
      true: 'With late fee',
      false: 'No late fee'
    },
    options: {
      ...tableColumnDefaultOption.options,
      filterKey: 'with_late_fee',
      customBodyRender: (value) => {
        const amount = parseFloat(value || 0)
        return amount > 0 ? <span>{amount.toFixed(2)}</span> : <span>-</span>
      }
    }
  },
  {
    name: 'payment_link',
    label: 'Payment Link',
    options: {
      ...tableColumnDefaultOption.options,
      disableFilter: true,
      customBodyRender: (value) =>
        value ? (
          <a target="_blank" rel="noopener noreferrer" href={value}>
            {value}
          </a>
        ) : null
    }
  },
  {
    name: 'invoice_items',
    label: 'Student Name',
    options: {
      ...tableColumnDefaultOption.options,
      customBodyRender: (value) => {
        const user = value?.[0]?.student?.user
        if (!user) return null
        return <span>{`${user.first_name || ''} ${user.last_name || ''}`}</span>
      },
      filterKey: 'child_name'
    }
  },
  {
    name: 'program_name',
    label: 'Program',
    options: {
      ...tableColumnDefaultOption.options,
      filterKey: 'program'
    }
  },
  {
    ...commonAttributes.date,
    name: 'enrolment_start_date',
    label: 'Enrolment Start Date',
    options: {
      ...commonAttributes.date.options,
      customBodyRender: (value) => (
        <span>{value ? dayjs(value).format('DD MMM, YYYY') : ''}</span>
      ),
      filterKey: 'enrolment_start_date'
    }
  },
  {
    name: 'academic_year_name',
    label: 'Academic Year',
    options: {
      ...tableColumnDefaultOption.options,
      customBodyRender: (value) => <span>{value ?? 'N/A'}</span>,
      filterKey: 'academic_year'
    }
  },
  {
    ...commonAttributes.date,
    name: 'due_date',
    label: 'Due Date',
    options: {
      ...commonAttributes.date.options,
      customBodyRender: (value) => (
        <span>{value ? dayjs(value).format('DD MMM, YYYY') : ''}</span>
      ),
      filterKey: 'payment_due_date'
    }
  },
  {
    ...commonAttributes.date,
    name: 'payment_date',
    label: 'Payment Date',
    options: {
      ...commonAttributes.date.options,
      customBodyRender: (value) => (
        <span>{value ? dayjs(value).format('DD MMM, YYYY') : ''}</span>
      ),
      filterKey: 'payment_date'
    }
  },
  {
    name: 'student_admission_installments',
    label: 'Payment Period',
    options: {
      ...tableColumnDefaultOption.options,
      customBodyRender: (value) => {
        const hasStartAndEnd = value?.[0]?.start_date && value?.[0]?.end_date
        if (hasStartAndEnd) {
          const from = dayjs(value?.[0]?.start_date).format('MMM YYYY')
          const to = dayjs(value?.[0]?.end_date).format('MMM YYYY')
          return <span>{`${from} - ${to}`}</span>
        }
        const windowStart = value?.[0]?.window_start_date
        if (windowStart) {
          const from = dayjs(windowStart)?.add(1, 'month')?.format('MMM YYYY')
          const to = dayjs(windowStart)?.add(3, 'month')?.format('MMM YYYY')
          return <span>{`${from} - ${to}`}</span>
        }
        return null
      },
      disableFilter: true
    }
  },
  // Feature 3 — daycare-billing badge + mid-admission toggle action.
  // The badge is read-only context for ops (preschool vs daycare mode).
  // The toggle action only renders on PENDING rows; the backend rejects
  // non-pending requests defensively.
  {
    name: 'student_admission_installments',
    label: 'Billing mode',
    options: {
      ...tableColumnDefaultOption.options,
      disableFilter: true,
      customBodyRender: (value, tableMeta) => {
        const isDaycare = Boolean(value?.[0]?.is_daycare_billing)
        const badgeLabel = isDaycare
          ? 'Issued in daycare mode'
          : 'Issued in preschool mode'
        return (
          <span style={{ whiteSpace: 'nowrap' }}>
            <span
              style={{
                fontSize: 12,
                padding: '2px 6px',
                borderRadius: 4,
                background: isDaycare ? '#e8f4ff' : '#f4f4f4',
                color: '#333'
              }}
            >
              {badgeLabel}
            </span>
          </span>
        )
      }
    }
  },
  {
    name: 'uuid',
    label: 'Switch billing mode',
    options: {
      ...tableColumnDefaultOption.options,
      disableFilter: true,
      customBodyRender: (uuid, tableMeta) => {
        const row = tableMeta?.tableData?.[tableMeta?.rowIndex]
        const status = row?.status
        const isDaycare =
          row?.student_admission_installments?.[0]?.is_daycare_billing
        // Only show the inline action on PENDING rows — past paid /
        // completed invoices are not reissued (Decision #3).
        if (status !== 'pending') {
          return <span>-</span>
        }
        const buttonLabel = isDaycare
          ? 'Switch to preschool billing'
          : 'Switch to daycare billing'
        return (
          <Button
            size="small"
            variant="outlined"
            color="primary"
            onClick={() => handleSwitchBillingMode(uuid)}
          >
            {buttonLabel}
          </Button>
        )
      }
    }
  }
]
